import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";

import {
  FormRow,
  FormSection,
} from "../../../../../components/layout/forms/Index";

import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { useCreatorStreams } from "../../../creators/hooks/useCreatorStreams";

import * as service from "../../../creators/services/creatorStreamsService";

import { compressImageFile } from "../../../../../utils/helpers/compression/imageCompressor";
import { uploadThumbnailToS3 } from "../../../../../utils/helpers/s3Uploader/uploadThumbnailToS3";

import "./CreatorCreateStreamForm.css";

const STREAM_TYPE_OPTIONS = [
  {
    value: "match",
    label: "Match",
  },
  {
    value: "training",
    label: "Training",
  },
  {
    value: "event",
    label: "Event",
  },
];

const ACCESS_TYPE_OPTIONS = [
  {
    value: "public",
    label: "Public",
  },
  {
    value: "unlisted",
    label: "Unlisted",
  },
  {
    value: "group",
    label: "Group",
  },
  {
    value: "private",
    label: "Private",
  },
];

const createInitialFormData = () => ({
  title: "",
  description: "",
  streamType: "match",
  accessType: "public",
  scheduledStart: "",
  recordingEnabled: false,
  thumbnailFile: null,
  thumbnailPreview: "",
});

const getRequestError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const CreatorCreateStreamForm = ({ username, onClose, onSuccess }) => {
  const { loading, error, createStream } = useCreatorStreams(username);

  const [formData, setFormData] = useState(createInitialFormData);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailProgress, setThumbnailProgress] = useState(0);

  const thumbnailPreviewRef = useRef("");

  const isBusy = loading || submitting || thumbnailUploading;
  const isSubmitDisabled = isBusy || !formData.title.trim();

  const errorMessage =
    formError ||
    (typeof error === "string" ? error : getRequestError(error, ""));

  useEffect(() => {
    thumbnailPreviewRef.current = formData.thumbnailPreview;
  }, [formData.thumbnailPreview]);

  useEffect(() => {
    return () => {
      if (thumbnailPreviewRef.current) {
        URL.revokeObjectURL(thumbnailPreviewRef.current);
      }
    };
  }, []);

  const clearFormError = () => {
    if (formError) {
      setFormError("");
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    clearFormError();

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files?.[0] || null;

    event.target.value = "";

    clearFormError();

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setFormError("Thumbnail must be an image.");

      return;
    }

    const preview = URL.createObjectURL(file);

    setFormData((current) => {
      if (current.thumbnailPreview) {
        URL.revokeObjectURL(current.thumbnailPreview);
      }

      return {
        ...current,
        thumbnailFile: file,
        thumbnailPreview: preview,
      };
    });
  };

  const handleRemoveThumbnail = () => {
    if (isBusy) {
      return;
    }

    setFormData((current) => {
      if (current.thumbnailPreview) {
        URL.revokeObjectURL(current.thumbnailPreview);
      }

      return {
        ...current,
        thumbnailFile: null,
        thumbnailPreview: "",
      };
    });

    setThumbnailProgress(0);
  };

  const uploadThumbnail = async () => {
    if (!formData.thumbnailFile) {
      return null;
    }

    setThumbnailUploading(true);
    setThumbnailProgress(0);

    const compressedFile = await compressImageFile({
      file: formData.thumbnailFile,
      maxWidth: 1280,
      maxHeight: 720,
      quality: 0.72,
      outputType: "image/jpeg",
    });

    const response = await service.createStreamThumbnailUploadUrl(
      compressedFile.type,
    );

    const { uploadUrl, thumbnailUrl } = response?.data || {};

    if (!uploadUrl || !thumbnailUrl) {
      throw new Error("Unable to create the thumbnail upload URL.");
    }

    await uploadThumbnailToS3({
      uploadUrl,
      file: compressedFile,
      onProgress: setThumbnailProgress,
    });

    setThumbnailProgress(100);

    return thumbnailUrl;
  };

  const resetForm = () => {
    if (thumbnailPreviewRef.current) {
      URL.revokeObjectURL(thumbnailPreviewRef.current);
    }

    thumbnailPreviewRef.current = "";

    setFormData(createInitialFormData());
    setFormError("");
    setThumbnailProgress(0);
  };

  const handleClose = () => {
    if (isBusy) {
      return;
    }

    resetForm();
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isBusy) {
      return;
    }

    const title = formData.title.trim();
    const description = formData.description.trim();

    if (!username) {
      setFormError("Creator information is missing.");

      return;
    }

    if (!title) {
      setFormError("Stream title is required.");

      return;
    }

    try {
      setSubmitting(true);
      setFormError("");

      const thumbnailUrl = await uploadThumbnail();

      const createdStream = await createStream({
        title,
        description,
        streamType: formData.streamType,
        accessType: formData.accessType,
        scheduledStart: formData.scheduledStart || null,
        recordingEnabled: formData.recordingEnabled ? 1 : 0,
        thumbnailUrl,
      });

      if (createdStream === false || createdStream?.success === false) {
        setFormError(createdStream?.message || "Failed to create stream.");

        return;
      }

      resetForm();

      await onSuccess?.(createdStream);

      onClose?.();
    } catch (submitError) {
      setFormError(getRequestError(submitError, "Failed to create stream."));
    } finally {
      setThumbnailUploading(false);
      setSubmitting(false);
    }
  };

  const progress = Math.min(
    100,
    Math.max(0, Math.round(Number(thumbnailProgress) || 0)),
  );

  const submitLabel = thumbnailUploading
    ? `Uploading ${progress}%`
    : submitting || loading
      ? "Creating..."
      : "Create Stream";

  return (
    <form
      className="creator-create-stream-form"
      onSubmit={handleSubmit}
      aria-busy={isBusy}
    >
      {errorMessage && (
        <div className="creator-create-stream-form__error" role="alert">
          {errorMessage}
        </div>
      )}

      <FormSection className="creator-create-stream-form__fields">
        <TextField
          label="Stream Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="Bucks vs Harlequins"
          maxLength={120}
          required
          disabled={isBusy}
        />

        <TextArea
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Add information about the stream"
          maxLength={1000}
          maxHeight={120}
          disabled={isBusy}
        />

        <FormRow>
          <Select
            label="Stream Type"
            name="streamType"
            value={formData.streamType}
            onChange={handleInputChange}
            items={STREAM_TYPE_OPTIONS}
            required
            disabled={isBusy}
          />

          <Select
            label="Access Type"
            name="accessType"
            value={formData.accessType}
            onChange={handleInputChange}
            items={ACCESS_TYPE_OPTIONS}
            required
            disabled={isBusy}
          />
        </FormRow>

        {formData.accessType === "private_group" && (
          <div className="creator-create-stream-form__note">
            Add the permitted groups from Broadcast Setup after creating the
            stream.
          </div>
        )}

        <TextField
          label="Scheduled Start"
          type="datetime-local"
          name="scheduledStart"
          value={formData.scheduledStart}
          onChange={handleInputChange}
          disabled={isBusy}
        />

        <label className="creator-create-stream-form__checkbox">
          <input
            type="checkbox"
            name="recordingEnabled"
            checked={formData.recordingEnabled}
            onChange={handleInputChange}
            disabled={isBusy}
          />

          <span>Enable recording after the live stream</span>
        </label>
      </FormSection>

      <section className="creator-create-stream-form__thumbnail">
        <div className="creator-create-stream-form__thumbnail-row">
          <div className="creator-create-stream-form__thumbnail-heading">
            <h3>Thumbnail</h3>
            <p>Optional · Recommended 1280 × 720</p>
          </div>

          {formData.thumbnailPreview ? (
            <div className="creator-create-stream-form__preview">
              <img
                src={formData.thumbnailPreview}
                alt="Stream thumbnail preview"
              />

              <button
                type="button"
                className="creator-create-stream-form__remove-thumbnail"
                onClick={handleRemoveThumbnail}
                disabled={isBusy}
                aria-label="Remove thumbnail"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="creator-create-stream-form__thumbnail-picker">
              <ImagePlus size={17} />

              <span>Add thumbnail</span>

              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleThumbnailChange}
                disabled={isBusy}
                hidden
              />
            </label>
          )}
        </div>

        {thumbnailUploading && (
          <div className="creator-create-stream-form__progress">
            <div className="creator-create-stream-form__progress-heading">
              <span>Uploading thumbnail</span>
              <strong>{progress}%</strong>
            </div>

            <div className="creator-create-stream-form__progress-track">
              <div
                className="creator-create-stream-form__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </section>

      <div className="creator-create-stream-form__actions">
        <button
          type="button"
          className="creator-create-stream-form__cancel"
          onClick={handleClose}
          disabled={isBusy}
        >
          Cancel
        </button>

        <button
          type="submit"
          className="creator-create-stream-form__submit"
          disabled={isSubmitDisabled}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default CreatorCreateStreamForm;
